import { ViewportScroller } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { PDFDocumentProxy } from 'ng2-pdf-viewer';
import {
  FormDataType,
  NgxExtendedPdfViewerComponent,
  NgxExtendedPdfViewerService,
  NgxExtendedPdfViewerModule,
} from 'ngx-extended-pdf-viewer';
import { Subject, takeUntil } from 'rxjs';

import { RawFormData } from '../../../interfaces/pdf-viewer.interface';
import { EditTemplateService } from '../../../services/edit-template/edit-template.service';
import { PdfPopulateService } from '../../../services/pdf-populate/pdf-populate.service';
import { PdfViewerSrcManagerService } from '../../../services/pdf-viewer-src-manager/pdf-viewer-src-manager.service';

@Component({
  selector: 'app-extended-pdf-viewer',
  templateUrl: './extended-pdf-viewer.component.html',
  styleUrls: ['./extended-pdf-viewer.component.scss'],
  imports: [NgxExtendedPdfViewerModule],
})
export class ExtendedPdfViewerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() inputIdHighlightActive: string;
  @Input() gutterPosition: number;
  @Input() disableEditing = false;
  @Input() fieldValues: any;

  @ViewChild('viewer', {
    static: false,
  })
  private pdfComponent: NgxExtendedPdfViewerComponent;
  @ViewChild('viewer', {
    read: ElementRef,
    static: false,
  })
  pdfElements: ElementRef;
  @ViewChild('viewer', {
    static: false,
  })
  viewerElement: ElementRef;

  PDFViewerApplication: any;

  @Output() pdfLoadedAndProxyEmitter = new EventEmitter<any>();
  @Output() ouputputIdHighlightActive = new EventEmitter<any>();
  @Output() textRenderedEmitter = new EventEmitter<void>();

  src: Uint8Array | string | ArrayBuffer;
  returnLink: string;
  pdfDocumentProxy: PDFDocumentProxy;

  previouslyHighlightedElementId = '';
  preventTextHighlight = false;

  fetchingFields: boolean;
  formData: FormDataType = {};
  rawFormData: RawFormData[];

  private _onDestroy = new Subject<void>();

  constructor(
    private pdfSrcService: PdfViewerSrcManagerService,
    public pdfPopulateService: PdfPopulateService,
    public editTemplateService: EditTemplateService,
    private viewportScroller: ViewportScroller,
    private renderer: Renderer2,
    private ngxService: NgxExtendedPdfViewerService,
  ) {}

  /**
   * Subscribe to the service that will fetch the binary for the viewer
   * and update the source so that the document can be rendered
   */
  bindToPdfSrcService(): void {
    this.pdfSrcService.pdfSrc
      .pipe(takeUntil(this._onDestroy))
      .subscribe((src: Uint8Array | null) => {
        if (src instanceof Uint8Array) this.src = src;
      });
  }

  /**
   * pdfLoadComplete() runs once the pdf source has been processed by viewer
   * but before rendering the text - this allows to update annotations before viewing
   * @param pdfDocumentProxy is a full pdf data emitted by viewer
   */
  pdfLoadComplete(pdfDocumentProxy: any): void {
    this.pdfDocumentProxy = pdfDocumentProxy;
    this.updateAnnotationsAfterPdfLoad(this.pdfPopulateService.fieldValues);
    this.pdfLoadedAndProxyEmitter.emit(pdfDocumentProxy);
  }

  /**
   * updateAnnotationsAfterPdfLoad() will update the currently loaded pdf text annotations
   * @param fieldValues is an object fetched from database after transforming
   */
  updateAnnotationsAfterPdfLoad(fieldValues: any): void {
    for (const key in fieldValues)
      this.updateAnnotationValueByID(
        fieldValues[key]?.FormFieldName[0],
        fieldValues[key]?.DataValue,
      );

    this.pdfPopulateService.populateFormData();
  }

  /**
   * updateAnnotationValueByID() will search through annotation storage and update specific one
   * if id doesn't exist, it will get created, annotations are readonly - it's the reason for this function
   * @param id is a string id in the annotation storage
   * @param value is a value that the annotation should hold
   */
  updateAnnotationValueByID(id: string, value: string | boolean | number | null): void {
    const { annotationStorage } = this.pdfDocumentProxy;
    if (annotationStorage)
      annotationStorage.setValue(id, {
        value,
      });
  }

  /**
   * afterTextRendered()
   * is called by pdf-viewer on each page's text-layer-rendered event
   * text-layer-renders after PDF loadComplete, making the elements available
   * by getElementById
   * @param event
   */
  async afterTextRendered(event: any): Promise<void> {
    const formIdToFieldName = await (this.pdfComponent as any)?.formIdToFieldName;
    this.pdfPopulateService.formIdToFieldName = {
      ...formIdToFieldName,
    };
    this.pdfPopulateService.populateFormData();
    this.readRawFormDescription();
    this.controlFormFields(event, 'text-layer-rendered');
    this.textRenderedEmitter.emit();
  }

  /**
   * removeFocus()
   * will clear all active highlighted fields on both the PDF and right side of edit document view
   */
  removeFocus(): void {
    const prevEl = document.getElementById(this.previouslyHighlightedElementId);
    if (prevEl) {
      this.renderer.setAttribute(prevEl, 'class', '');
      this.previouslyHighlightedElementId = '';
      this.ouputputIdHighlightActive.emit(null);
    }
  }

  /**
   * onFocus()
   * will highlight the corresponding fields on either side when focused
   * and scroll to the field if not in view
   * @param event
   */
  onFocus(event: any): void {
    const id = event.srcElement.id;
    const rightAnchor: string = id.includes('Right-') ? id : 'Right-' + id;

    this.viewportScroller.scrollToAnchor(rightAnchor);
    // OutputIdHighlightActive also disables highlights on the previous input
    this.ouputputIdHighlightActive.emit(event.srcElement.id);

    if (this.previouslyHighlightedElementId) {
      const prevEl = document.getElementById(this.previouslyHighlightedElementId);
      if (prevEl) this.renderer.setAttribute(prevEl, 'class', '');
    }
    const el = document.getElementById(event.srcElement.id);
    el?.blur(); /* blur() prevents the PDF inputs from being editable */
    this.renderer.setAttribute(el, 'class', 'inputIdHighlightActive');
    this.previouslyHighlightedElementId = event.srcElement.id;
    this.checkboxValuesSync(id, rightAnchor);
  }

  /**
   * checkboxValuesSync() will assign the same value to the form side checkbox as PDF
   * by its values - it's required due to the text fields being not editable on the PDF
   * as opposed to the checkboxes that should update the data in the memory
   * @param pdfCheckboxID from the PDF side Input field
   * @param formCheckboxID from the form (right) side Input
   * @returns void
   */
  checkboxValuesSync(pdfCheckboxID: string, formCheckboxID: string): void {
    if (this.disableEditing === true || !this.fieldValues || !this.pdfDocumentProxy) return;
    const fieldToUpdate = Object.keys(this.fieldValues).filter(
      (field: any) =>
        this.fieldValues[field].AnchorID === formCheckboxID &&
        this.fieldValues[field].DataTypeName === 'BIT',
    );
    if (fieldToUpdate === undefined || fieldToUpdate.length === 0) return;
    const clickedInputAnnotation =
      this.pdfDocumentProxy.annotationStorage?.getRawValue(pdfCheckboxID);
    this.fieldValues[fieldToUpdate?.[0]].DataValue = !clickedInputAnnotation;
  }

  onFocusOut(event: any): void {}

  onBlur(event: any): void {}

  /**
   * addPdfInputListeners()
   * Adds listeners to each input element, including onFocus, onFocusout and onBlur.
   * @param inputElement
   */
  addPdfInputListeners(inputElement: any) {
    // add only one onFocus listener
    if (inputElement.getAttribute('data-on-focus-event-listener') !== 'true') {
      inputElement.setAttribute('data-on-focus-event-listener', 'true');
      inputElement.addEventListener('focus', this.onFocus.bind(this));
    }
    // add only one onfocusout listener
    if (inputElement.getAttribute('data-on-focusout-event-listener') !== 'true') {
      inputElement.setAttribute('data-on-focusout-event-listener', 'true');
      inputElement.addEventListener('focusout', this.onFocusOut.bind(this));
    }
    // add only one onBlur listener
    if (inputElement.getAttribute('data-on-blur-event-listener') !== 'true') {
      inputElement.setAttribute('data-on-blur-event-listener', 'true');
      inputElement.addEventListener('blur', this.onBlur.bind(this));
    }
    // add only one onClick listener has been commented out because it may override all clicks
    if (inputElement.getAttribute('data-on-click-event-listener') !== 'true')
      inputElement.setAttribute('data-on-click-event-listener', 'true');
  }

  /**
   * controlFormFields()
   * Selects all input tags and attached input listeners to each input type.
   * @param event
   * @param eventType
   */
  controlFormFields(event: any, eventType: string) {
    //grab all input elements - by text area in the PDF using async/await because there may be a ton of elements
    (async () => {
      const HtmlCollection = await this.pdfElements.nativeElement.getElementsByTagName('textarea');
      // if HTML collection has input elements then add onFocus and onBlur listeners to each
      if (HtmlCollection.length > 0)
        for (const inputElement of HtmlCollection) this.addPdfInputListeners(inputElement);
    })();

    // grab all input elements in the PDF using async/await because there may be a ton of elements
    (async () => {
      const HtmlCollection = await this.pdfElements.nativeElement.getElementsByTagName('input');
      // if HTML collection has input elements then add onFocus and onBlur listeners to each
      if (HtmlCollection.length > 0)
        for (const inputElement of HtmlCollection) this.addPdfInputListeners(inputElement);
    })();
  }

  /**
   * readRawFormDescription()
   * reads the array of form data from the PDF service
   * and maps it to a format compatible with the template fields grid
   */
  async readRawFormDescription(): Promise<void> {
    const raw = await this.ngxService.getFormData(true);
    this.rawFormData = raw.map((annotation: any) => ({
      ID: annotation.fieldAnnotation.id,
      FIELDNAME: annotation.fieldAnnotation.fieldName,
      FIELDTYPE: annotation.fieldAnnotation.fieldType,
      SUBTYPE: annotation.fieldAnnotation.alternativeText,
      DESCRIPTION: '',
      MAXCHARACTERS: undefined,
      BOFIELDNAME: undefined,
      REQUIRED: false,
      HIDDEN: false,
    }));
    this.editTemplateService.updateJson([...this.rawFormData]);
  }

  ngOnInit(): void {
    this.bindToPdfSrcService();
    this.pdfPopulateService.formData.subscribe((res: any) => {
      this.formData = res;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.PDFViewerApplication = (window as any)?.PDFViewerApplication;
    this.pdfDocumentProxy = this.PDFViewerApplication?.pdfDocument;
    for (const propName in changes)
      if (changes.hasOwnProperty(propName)) {
        const change = changes[propName];
        switch (propName) {
          case 'inputIdHighlightActive': {
            const currentChange = change.currentValue?.includes('Right-')
              ? change.currentValue.split('-')[1]
              : change.currentValue;
            const previousChange = change.previousValue?.includes('Right-')
              ? change.previousValue.split('-')[1]
              : change.previousValue;
            // will scroll PDF to the field with the matching anchorID using currentValue
            if (currentChange) {
              // if previousValue exists, it will remove the highlight class both from PDF and fields
              if (this.previouslyHighlightedElementId) {
                const prevEl = document.getElementById(this.previouslyHighlightedElementId);
                if (this.renderer != null && prevEl)
                  this.renderer.setAttribute(prevEl, 'class', '');
              }

              // applying the highlight class to the PDF field based upon currentValue
              const el = document.getElementById(currentChange)
                ? document.getElementById(currentChange)
                : null;
              if (this.renderer != null && el)
                this.renderer.setAttribute(el, 'class', 'inputIdHighlightActive');
              this.viewportScroller.scrollToAnchor(currentChange);
              this.previouslyHighlightedElementId = currentChange;

              // Removing class from fields if there is a previousValue but not current one (value was unfocused)
            } else if (previousChange) {
              if (this.previouslyHighlightedElementId) {
                const prevEl = document.getElementById(this.previouslyHighlightedElementId);
                if (this.renderer != null && prevEl)
                  this.renderer.setAttribute(prevEl, 'class', '');
              }
              this.previouslyHighlightedElementId = '';
            }
            break;
          }
          default: {
          }
        }
      }
  }

  ngOnDestroy() {
    delete (window as any).pdfWorkerSrc;
    this.pdfSrcService.resetSource();
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
